import {
  AssetsProvider,
  Head,
  RESOURCE_LINKS,
  useRouter,
  useTranslation,
} from '@obvio/app'
import { resolveResourceLinks, serverClient } from '@obvio/server'
import dynamic from 'next/dynamic'

import { schema } from '@/api/schema'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { HiddenHeading } from '@/components/HiddenHeading'
import { JsonLd } from '@/components/JsonLd'
import { Layout } from '@/components/Layout'
import { NavFormEvent } from '@/components/NavForms/NavFormEvent'
import { NavFormNewsletter } from '@/components/NavForms/NavFormNewsletter'
import { PageSeo } from '@/components/PageSeo'
import { Renderer } from '@/components/Renderer'
import { RESOURCE_PREFIXES } from '@/constants'
import { RESTAURANT, WINERY } from '@/constants/hotel'
import { serverPrefetchQueries } from '@/constants/queries'
import { REVALIDATE_SECONDS } from '@/constants/revalidate'
import { restaurantJsonLd, wineryJsonLd } from '@/utils/jsonLd'
import {
  describePage,
  pageHasHeading,
  toSiteLocale,
  withSiteName,
} from '@/utils/seo'

import type { LayoutType } from '@/components/Layout'
import type { RendererData } from '@obvio/layout/renderer'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

const LazyInfoPageSuffix = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "LazyInfoPageSuffix" */
    '@/components/InfoPageSuffix'
  )
  return comp.InfoPageSuffix
})

function Page({
  page,
}: {
  page: {
    id: string
    slug: string
    data: RendererData<any, any>[]
    layoutRef: { title: LayoutType }
    title?: string | null
    seoTitle?: string | null
    seoDescription?: string | null
  }
}): ReactElement {
  const { hasParam } = useRouter()
  const { locale } = useTranslation()
  const siteLocale = toSiteLocale(locale)

  const isInfo = page.layoutRef.title === 'Informacje'
  const isHome = page.slug === ''
  const title =
    page.seoTitle ||
    (!isHome && page.title ? withSiteName(page.title) : undefined)
  const description = page.seoDescription || describePage(page.data)
  const headingFallback = page.title || page.seoTitle

  return (
    <>
      <PageSeo path={page.slug ?? ''} title={title} description={description} />
      {!isHome && headingFallback ? (
        <Breadcrumbs trail={[{ name: headingFallback, path: page.slug }]} />
      ) : null}
      {page.slug === RESTAURANT.path ? (
        <JsonLd id="restaurant" data={restaurantJsonLd(siteLocale)} />
      ) : null}
      {page.slug === WINERY.path ? (
        <JsonLd id="winery" data={wineryJsonLd(siteLocale)} />
      ) : null}
      {page.slug === '' ? (
        <Head>
          <meta
            name="google-site-verification"
            content="mJpXX-8exFdoLD6VeTKeFqi0TF6mpa009gY-_KIh550"
          />
        </Head>
      ) : null}
      <Layout type={page.layoutRef.title}>
        {!pageHasHeading(page.data) && headingFallback ? (
          <HiddenHeading>{headingFallback}</HiddenHeading>
        ) : null}
        <AssetsProvider path={`/page/${page.id}`}>
          {hasParam('id') ? (
            <div />
          ) : (
            <Renderer key={page.id} data={page.data} />
          )}
          {isInfo ? <LazyInfoPageSuffix /> : <div />}
          <NavFormEvent />
          <NavFormNewsletter />
        </AssetsProvider>
      </Layout>
    </>
  )
}

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string[] }>,
): Promise<
  GetStaticPropsResult<{
    page: {
      id: string
      data: string
      layoutRef: { title: string }
    }
  }>
> {
  const client = await serverClient(schema, { ...ctx, isIsr: true })

  const slug = ctx?.params?.slug ?? ['']

  const page = await client.query('getPage', {
    args: {
      slug: slug.join('/'),
    },
    select: {
      slug: true,
      title: true,
      data: true,
      layoutRef: {
        title: true,
      },
      seoTitle: true,
      seoDescription: true,
    },
  })

  await serverPrefetchQueries(page?.data ?? [], client)

  if (!page) {
    return { notFound: true }
  }

  return {
    ...client.ssrData({
      page,
      [RESOURCE_LINKS]: await resolveResourceLinks(
        page.data,
        RESOURCE_PREFIXES,
      ),
    }),
    revalidate: REVALIDATE_SECONDS,
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })

  const paths = await client.query('getStaticPaths')

  return {
    paths: paths
      .filter((slug) => slug !== 'pokoje')
      .map((slug) => ({
        params: { slug: slug.split('/') },
      })),
    fallback: 'blocking',
  }
}

export default Page
